import './index.scss'
// The same pills volunteers' roles are picked with.
import '@pages/v2/Volunteering/TeamsPage/RolePicker/index.scss'

import { useEffect, useRef, useState } from 'react'

import { useAuth } from '@auth/AuthProvider'
import Modal from '@components/Modal'
import StaffController from '@data/controller/StaffController'
import { ChurchStaff, StaffRole, allStaffRoles } from '@data/interfaces/church.interfaces'

import closeIcon from 'url:@icon/close.svg';

// Invites a new staff member, or edits one when `staff` is given. Styled after
// the Create in Salt event and team forms. The caller keys it per open, so
// every open starts from the member as they are now.
export default function StaffMemberModal(props: {
  isOpen: boolean,
  staff?: ChurchStaff | null,
  close: () => void,
  onSaved: () => void
}) {
  const auth = useAuth();
  const isEditing = Boolean(props.staff);

  const [name, setName] = useState(props.staff?.name ?? '');
  const [email, setEmail] = useState(props.staff?.email ?? '');
  const [roles, setRoles] = useState<StaffRole[]>(props.staff?.roles ?? []);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (props.isOpen) {
      nameInputRef.current?.focus({ preventScroll: true });
    }
  }, [props.isOpen]);

  // Legacy roles a member still holds are shown alongside the current ones, so
  // they can be seen and taken away.
  const legacyRoles = roles.filter((role) => !allStaffRoles.includes(role));
  const pickableRoles = [...allStaffRoles, ...legacyRoles];

  const trimmedName = name.trim();
  const trimmedEmail = email.trim();

  const hasChanges = !props.staff ||
    trimmedName !== props.staff.name ||
    roles.length !== props.staff.roles.length ||
    roles.some((role) => !props.staff!.roles.includes(role));

  const canSave = trimmedName.length > 0 &&
    (isEditing || trimmedEmail.includes('@')) &&
    roles.length > 0 &&
    hasChanges &&
    !isSaving;

  const toggleRole = (role: StaffRole) => {
    setRoles((currentRoles) => currentRoles.includes(role) ?
      currentRoles.filter((heldRole) => heldRole !== role) :
      [...currentRoles, role]);
  };

  const save = async () => {
    if (!auth.churchId) {
      setError('Missing church context. Please refresh and try again.');
      return;
    }

    if (!canSave) return;

    setIsSaving(true);
    setError(null);

    try {
      const response = props.staff ?
        await StaffController.editStaffMember(auth.churchId, props.staff.id, trimmedName, roles, auth) :
        await StaffController.inviteStaffMember(auth.churchId, trimmedName, trimmedEmail, roles, auth);

      if (response.error) {
        setError(response.error);
        return;
      }

      props.onSaved();
    } finally {
      setIsSaving(false);
    }
  };

  let submitTitle = isEditing ? 'Save Changes' : 'Send Invite';
  if (isSaving) {
    submitTitle = isEditing ? 'Saving...' : 'Sending...';
  }

  return <Modal
    id='staff-member-modal'
    isOpen={props.isOpen}
    footerButtons={[]}>
    <div className='staff-member-modal-content'>
      <div className='header'>
        <button
          type='button'
          className='header-control close'
          disabled={isSaving}
          onClick={props.close}>
          <img src={closeIcon} alt='Close icon' />
        </button>

        <h3 className='title'>{isEditing ? 'Edit Staff Member' : 'Invite Staff Member'}</h3>

        <span className='header-control-spacer' />
      </div>

      <form
        className='staff-form'
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}>
        {error ? <p className='save-error'>{error}</p> : null}

        <div className='section'>
          <div className='input'>
            <input
              type='text'
              name='name'
              placeholder='Name'
              aria-label='Name'
              autoComplete='off'
              value={name}
              ref={nameInputRef}
              disabled={isSaving}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className='separator' />

          <div className='input'>
            <input
              type='email'
              name='email'
              placeholder='Email'
              aria-label='Email'
              autoComplete='off'
              value={email}
              // The account is tied to its email, so it can't be changed here.
              disabled={isSaving || isEditing}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>

        <h4 className='section-title'>Roles</h4>

        <div className='section'>
          <div className='role-picker' role='group' aria-label='Roles'>
            {pickableRoles.map((role) => {
              const isSelected = roles.includes(role);

              return <button
                key={role}
                type='button'
                className={isSelected ? 'role-chip is-selected' : 'role-chip'}
                aria-pressed={isSelected}
                disabled={isSaving}
                onClick={() => toggleRole(role)}>
                {role}
              </button>
            })}
          </div>
        </div>

        <p className='footnote'>
          {isEditing ?
            'Roles decide which parts of Church Hub this person can see and change.' :
            'Roles determine what this staff member has access to within Church Hub. You can always add or remove them later.'}
        </p>

        <button type='submit' className='submit' disabled={!canSave}>
          {submitTitle}
        </button>
      </form>
    </div>
  </Modal>
}
